import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Plus, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeLabels = { template: 'Template', element: 'Element', blog: 'Blog' };

/**
 * Category select with "Add new category" inline.
 * When a new category is created, onCategoryCreated is called with the new category
 * and onValueChange is called with the new category id so the form can select it.
 */
const CategorySelectWithAdd = ({
  categories = [],
  value,
  onValueChange,
  type,
  onCategoryCreated,
  placeholder = 'Select category',
  label = 'Category',
  required = false,
}) => {
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const typeLabel = typeLabels[type] || type;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast({ title: 'Error', description: 'Category name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!API_URL) {
        throw new Error('REACT_APP_BACKEND_URL is not set. Check your frontend .env file.');
      }
      let res;
      try {
        res = await fetch(`${API_URL}/api/admin/content/categories`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name,
            type,
            description: newDescription.trim() || undefined,
            order: (categories.length + 1) || 0,
          }),
        });
      } catch {
        toast({
          title: 'Cannot reach server',
          description: `Unable to connect to the backend at ${API_URL}. Make sure the backend API is running.`,
          variant: 'destructive',
        });
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        const msg = data.detail
          ? (Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(', ') : data.detail)
          : 'Failed to create category';
        toast({ title: `Error (${res.status})`, description: msg, variant: 'destructive' });
        return;
      }
      if (onCategoryCreated) onCategoryCreated(data);
      onValueChange(data.id);
      setAddOpen(false);
      setNewName('');
      setNewDescription('');
      toast({ title: 'Category created', description: `"${data.name}" is now available` });
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Something went wrong', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
      <div className="flex gap-2">
        <Select value={value || ''} onValueChange={onValueChange}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setAddOpen(true)}
          title={`Add new ${typeLabel} category`}
          className="shrink-0"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add new {typeLabel} category</DialogTitle>
            <DialogDescription>
              Create a category to use here. It will be available in the Categories list too.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-cat-name">Name *</Label>
              <Input
                id="new-cat-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Social Media"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-cat-desc">Description (optional)</Label>
              <Input
                id="new-cat-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Short description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategorySelectWithAdd;
